import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CacheService } from '../services/cache.service';
import { Observable } from 'rxjs';
import {
  AppraisalRepAssetToAddInCluster,
  AppraisalReportClusterIndexModel,
  AssetIdentificationDataModel,
  AssetLocalizationInformationModel,
  AssetStayAndFacilityInformationModel,
  AssetSurfacesInformationModel,
  AssetTabMassiveResultModel,
  AssetTabModel,
  AssetTenureInformationModel,
  AssetTreeNodeExtendedIndexModel,
  AssetTypologyAndStateInformationDto,
  AssetTypologyInformationDto,
  AssetWorkPlanStateModel,
  AssetWorkStates,
  CheckSurfacesForWorkPlanModel,
  ClusterInitialDetailModel,
  ClusterTypes,
  ClusterValorationSummary,
  CommonAssetDataIndexModel,
  FoxeetValidationFilterModel,
  MassiveValorationsBaseModel,
  MultipleAssetRecalculationReportDataModel,
  OrNull,
  OrUndef,
  RelatedDeliverableDto,
  ValidationModel,
  ValidationReportModel,
  ValorationMethods,
  WorkflowItemReportAssetClusterAssetsCreateModel,
  WorkflowItemReportAssetClusterEditModel,
  WorkflowItemReportClusterModel,
  WorkflowItemReportAssetClusterTreeModel,
} from '@foxeet/domain';
import { WorkflowService } from '../services/workflow.service';

@Injectable()
export class AppraisalReportService extends WorkflowService {
  constructor(http: HttpClient, cache: CacheService) {
    super(http, 'AppraisalReports', cache);
  }

  getInitialDetails<T>(id: number): Observable<T> {
    return this.get<T>(`${id}/InitialDetails`);
  }

  duplicate(reportId: number) {
    return this.post('Duplicate', null, { params: this.setParams({ reportId }) });
  }

  generateDraftReport(reportId: number): Observable<Blob> {
    return this.get(`${reportId}/DraftReport`, { responseType: 'blob', observe: 'response' });
  }

  getTreeExtendedByReportId(reportId: number): Observable<AssetTreeNodeExtendedIndexModel[]> {
    return this.get<AssetTreeNodeExtendedIndexModel[]>(`${reportId}/TreeExtended`);
  }

  getCommonAssetDataByReportId(reportId: number): Observable<CommonAssetDataIndexModel> {
    return this.get<CommonAssetDataIndexModel>(`${reportId}/CommonAsset`);
  }

  public getAppraisalAssetsFromAppraisalRequestElements(reportId: number): Observable<AssetTreeNodeExtendedIndexModel[]> {
    return this.post<null, AssetTreeNodeExtendedIndexModel[]>(`${reportId}/AppraisalAssetsFromAppraisalRequestElements`, null);
  }

  getClusterInitialDetails(reportId: number, id: number): Observable<ClusterInitialDetailModel> {
    return this.get<ClusterInitialDetailModel>(`${reportId}/Clusters/${id}/InitialDetails`);
  }

  public createAssetCluster(reportId: number, cluster: WorkflowItemReportAssetClusterEditModel): Observable<void> {
    return this.post<WorkflowItemReportAssetClusterEditModel, void>(`${reportId}/Clusters`, cluster);
  }

  public getClusterSummary(page: number, pagesize: number, body = {}) {
    return this.post<any, any>('Clusters/ListPagedAssetsSummary', body, {
      params: this.setParams({
        page,
        pagesize,
      }),
    });
  }

  // ESTE ES EL ENDPOINT QUE TIENE LOGICA NUEVA
  public editAssetCluster(reportId: number, cluster: WorkflowItemReportAssetClusterEditModel): Observable<void> {
    return this.put<WorkflowItemReportAssetClusterEditModel, void>(`${reportId}/Clusters/${cluster.id}`, cluster);
  }

  // ESTE ES EL ENDPOINT QUE TIENE LOGICA NUEVA
  public editAssetClusterAdoptedUnit(reportId: number, cluster: WorkflowItemReportAssetClusterEditModel): Observable<void> {
    return this.put<WorkflowItemReportAssetClusterEditModel, void>(`${reportId}/Clusters/${cluster.id}/AdoptedUnit`, cluster);
  }

  public listAllClusters(reportId: number): Observable<AppraisalReportClusterIndexModel[]> {
    return this.get<AppraisalReportClusterIndexModel[]>(`${reportId}/Clusters`);
  }

  public deleteAssetCluster(reportId: number, clusterId: number): Observable<any> {
    return this.delete<any>(`${reportId}/Clusters/${clusterId}`);
  }

  public getClusterValorationSummary(reportId: number, clusterId: number): Observable<ClusterValorationSummary> {
    return this.get<ClusterValorationSummary>(`${reportId}/Clusters/${clusterId}/ValorationSummary`);
  }

  public getClusterDetail(reportId: OrUndef<number>, clusterId: OrUndef<number>): Observable<WorkflowItemReportClusterModel> {
    return this.get<WorkflowItemReportClusterModel>(`${reportId}/Clusters/${clusterId}`);
  }

  public getTerrainsToAddInCluster(reportId: number): Observable<AppraisalRepAssetToAddInCluster[]> {
    return this.get<AppraisalRepAssetToAddInCluster[]>(`${reportId}/TerrainsToAddInCluster`);
  }

  public removeAssetFromCluster(reportId: number, clusterId: number, assetId: number): Observable<any> {
    return this.delete<any>(`${reportId}/Clusters/${clusterId}/Assets/${assetId}`);
  }

  addAssetToCluster(reportId: number, clusterId: number, assetIds: number[]): Observable<void> {
    const body: WorkflowItemReportAssetClusterAssetsCreateModel = {
      assetIds,
    };
    return this.put<WorkflowItemReportAssetClusterAssetsCreateModel, void>(`${reportId}/Clusters/${clusterId}/Assets`, body);
  }

  getWorkPlanStateCluster(reportId: number, clusterId: number): Observable<AssetWorkPlanStateModel> {
    return this.get<AssetWorkPlanStateModel>(`${reportId}/Clusters/${clusterId}/WorkPlanState`);
  }
  editWorkPlanStateCluster(reportId: number, clusterId: number, body: AssetWorkPlanStateModel): Observable<AssetWorkPlanStateModel> {
    return this.put<AssetWorkPlanStateModel, AssetWorkPlanStateModel>(`${reportId}/Clusters/${clusterId}/WorkPlanState`, body);
  }
  calculateWorkPlanState(reportId: number, clusterId: number, data: AssetWorkPlanStateModel): Observable<AssetWorkPlanStateModel> {
    return this.post<typeof data, AssetWorkPlanStateModel>(`${reportId}/Clusters/${clusterId}/CalculateWorkPlanState`, data);
  }

  public calculateWorkPlanStateFromForm(reportId: number, clusterId: number, data: AssetWorkPlanStateModel): Observable<AssetWorkPlanStateModel> {
    return this.post<typeof data, AssetWorkPlanStateModel>(`${reportId}/Clusters/${clusterId}/CalculateWorkPlanStateFromForm`, data);
  }

  public checkSurfacesForWorkPlan(reportId: number, clusterId: number): Observable<CheckSurfacesForWorkPlanModel> {
    return this.get<CheckSurfacesForWorkPlanModel>(`${reportId}/Clusters/${clusterId}/CheckSurfacesForWorkPlan`);
  }

  getDeliverablesByReportId(id: number): Observable<RelatedDeliverableDto> {
    return this.get<RelatedDeliverableDto>(`${id}/Deliverables`);
  }

  getValorationSummary(id: number, isFutureValoration: boolean = false): Observable<any> {
    return this.get<any>(`${id}/ValorationSummary`, { params: this.setParams({ isFutureValoration }) });
  }

  duplicateFromAppraisal(targetId: number, fromId: number): Observable<void> {
    return this.post<null, void>('DuplicateFromAppraisal', null, {
      params: this.setParams({
        targetId,
        fromId,
      }),
    });
  }

  public getAssetTreeFilteredByClusterType(
    reportId: number,
    clusterType: ClusterTypes,
    workState?: AssetWorkStates,
    clusterId: OrNull<number> = null,
  ): Observable<WorkflowItemReportAssetClusterTreeModel[]> {
    return this.get<WorkflowItemReportAssetClusterTreeModel[]>(`${reportId}/Clusters/GetClusterTree`, {
      params: this.setParams({
        clusterType,
        workState,
        clusterId,
      }),
    });
  }

  public isOutOfUsefulLife(reportId: number, clusterId: number, queryParams: { [key: string]: any }): Observable<CheckSurfacesForWorkPlanModel> {
    return this.post<null, CheckSurfacesForWorkPlanModel>(`${reportId}/Clusters/${clusterId}/IsOutOfUsefulLife`, null, { params: this.setParams(queryParams) });
  }

  /**
   * @Todo Circular dependency Filter @foxeet/utils -> @foxeet/data-access -> @foxeet/utils
   * De momento no se usa, si se necesitase habría que pasar el filtro desde fuera o mover Filter a data-access
   */
  // public getFilteredValidations(id: string | number, filter: Filter) {
  //   const { page = 1, size: pagesize = 10, data = {} } = filter?.getCurrentFilter() ?? {};
  //   const body = removeNullsAndUndefined(data);
  //   return this.post<typeof body, ValidationModel[]>(`${id}/Validations/ListPaged`, body, {
  //     params: this.setParams({
  //       page,
  //       pagesize,
  //       id,
  //     }),
  //   });
  // }

  public getAllValidations(id: string | number, body?: Partial<FoxeetValidationFilterModel>) {
    return this.post<typeof body, ValidationModel[]>(`${id}/Validations/ListAll`, body ?? {});
  }

  public calculateReportValidationAlerts(id: number) {
    return this.post<void, ValidationReportModel>(`${id}/Validations/Validate`);
  }

  // Massive edition
  // Tabs endpoints

  getDetailsMassiveEditionIdentification = (reportId: number): Observable<AssetTabModel<AssetIdentificationDataModel>[]> => {
    return this.get<AssetTabModel<AssetIdentificationDataModel>[]>(`${reportId}/MassiveAssetEdits/Identification`);
  };

  editMassiveEditionIdentification = (id: number, assets: AssetTabModel<AssetIdentificationDataModel>[]): Observable<AssetTabMassiveResultModel[]> => {
    return this.put<AssetTabModel<AssetIdentificationDataModel>[], AssetTabMassiveResultModel[]>(`${id}/MassiveAssetEdits/Identification`, assets);
  };

  getDetailsMassiveEditionLocalization = (reportId: number): Observable<AssetTabModel<AssetLocalizationInformationModel>[]> => {
    return this.get<AssetTabModel<AssetLocalizationInformationModel>[]>(`${reportId}/MassiveAssetEdits/Localization`);
  };

  editMassiveEditionLocalization = (id: number, assets: AssetTabModel<AssetLocalizationInformationModel>[]): Observable<AssetTabMassiveResultModel[]> => {
    return this.put<AssetTabModel<AssetLocalizationInformationModel>[], AssetTabMassiveResultModel[]>(`${id}/MassiveAssetEdits/Localization`, assets);
  };

  getDetailsMassiveEditionSurfaces = (reportId: number): Observable<AssetTabModel<AssetSurfacesInformationModel>[]> => {
    return this.get<AssetTabModel<AssetSurfacesInformationModel>[]>(`${reportId}/MassiveAssetEdits/Surface`);
  };

  editMassiveEditionSurfaces = (id: number, assets: AssetTabModel<AssetSurfacesInformationModel>[]): Observable<AssetTabMassiveResultModel[]> => {
    return this.put<AssetTabModel<AssetSurfacesInformationModel>[], AssetTabMassiveResultModel[]>(`${id}/MassiveAssetEdits/Surface`, assets);
  };

  getDetailsMassiveEditionTypology = (reportId: number): Observable<AssetTabModel<AssetTypologyInformationDto>[]> => {
    return this.get<AssetTabModel<AssetTypologyAndStateInformationDto>[]>(`${reportId}/MassiveAssetEdits/Typology`);
  };

  editMassiveEditionTypology = (id: number, assets: AssetTabModel<AssetSurfacesInformationModel>[]): Observable<AssetTabMassiveResultModel[]> => {
    return this.put<AssetTabModel<AssetSurfacesInformationModel>[], AssetTabMassiveResultModel[]>(`${id}/MassiveAssetEdits/Typology`, assets);
  };

  getDetailsMassiveEditionAssetStayAndFacilityInformation = (reportId: number): Observable<AssetTabModel<AssetStayAndFacilityInformationModel>[]> => {
    return this.get<AssetTabModel<AssetStayAndFacilityInformationModel>[]>(`${reportId}/MassiveAssetEdits/AssetStayAndFacilityInformation`);
  };

  editMassiveEditionAssetStayAndFacilityInformation = (id: number, assets: AssetTabModel<AssetStayAndFacilityInformationModel>[]): Observable<AssetTabMassiveResultModel[]> => {
    return this.put<AssetTabModel<AssetStayAndFacilityInformationModel>[], AssetTabMassiveResultModel[]>(`${id}/MassiveAssetEdits/AssetStayAndFacilityInformation`, assets);
  };

  getDetailsMassiveEditionTenures = (reportId: number): Observable<AssetTabModel<AssetTenureInformationModel>[]> => {
    return this.get<AssetTabModel<AssetTenureInformationModel>[]>(`${reportId}/MassiveAssetEdits/Tenures`);
  };

  editMassiveEditionTenures = (id: number, assets: AssetTabModel<AssetTenureInformationModel>[]): Observable<AssetTabMassiveResultModel[]> => {
    return this.put<AssetTabModel<AssetTenureInformationModel>[], AssetTabMassiveResultModel[]>(`${id}/MassiveAssetEdits/Tenures`, assets);
  };

  recalculateAll = (reportId: number): Observable<MultipleAssetRecalculationReportDataModel> => {
    return this.get(`${reportId}/RecalculateAllValorations`);
  };

  getMassiveValorationMethods = (reportId: number, valorationMethod: ValorationMethods, isFutureValoration: boolean) => {
    return this.get(`${reportId}/MassiveValorations/Valorations`, {
      params: this.setParams({
        valorationMethod,
        isFutureValoration,
      }),
    });
  };

  editMassiveValorationsMethods = (
    reportId: number,
    data: MassiveValorationsBaseModel<any>[],
    valorationMethod?: ValorationMethods,
    isFutureValoration?: boolean,
  ): Observable<any[]> => {
    return this.put(`${reportId}/MassiveValorations/Valorations`, data, {
      params: this.setParams({
        valorationMethod,
        isFutureValoration,
      }),
    });
  };

  updateLastUpdateDate(reportId: number) {
    return this.patch(`${reportId}/UpdateLastUpdateDate`);
  }
}
